import type { CandidateReply } from './candidateReplies';
import { COLORS, fmtSlotStart } from './format';

/**
 * "They wrote back" — on the candidate's card in 📅 Scheduling.
 *
 * The reply lands in 📥 Requests and the interview is booked here, so a recruiter working the
 * scheduling list had no sign an answer had arrived (Theresa, Sep 2026). Clicking opens the
 * thread in Gmail, which is the context the chip deliberately does not try to summarise.
 *
 * TWO STATES, and the difference is whether we can prove what they said:
 *
 *  · the reply names one of the times WE offered → the chip names it back, in our own rendering
 *    of that slot rather than in their words;
 *  · anything else → "replied", and the recruiter reads it.
 *
 * It never paraphrases. A chip that says "confirmed Wed 10:00" because "Wednesday" appeared in a
 * sentence is worse than no chip: it is a booking instruction, and the recruiter would act on it.
 * See candidateReplies.replyNamesSlot for exactly how narrow the match is.
 */
export function ReplyChip({ reply }: { reply: CandidateReply | undefined }) {
  if (!reply) return null;
  const confirmed = reply.confirmedSlots;
  const label = confirmed.length
    ? `✉️ Confirmed ${confirmed.map(fmtSlotStart).join(' · ')}`
    : '✉️ Replied';
  return (
    <a
      href={reply.gmailUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold hover:opacity-80"
      style={
        confirmed.length
          ? { backgroundColor: '#DCF5E4', color: '#136C34' }
          : { backgroundColor: COLORS.blueBg, color: COLORS.blueText }
      }
      // Their own words, untouched — the tooltip is the one place the message itself shows.
      title={`${reply.question.slice(0, 300)}\n\nOpen the email in Gmail`}
    >
      {label}
    </a>
  );
}
